import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "./../../firebase.js";
import "./Voting.css";
import { getStoredAccessMode, isPremiumAccess } from "../../utils/accessMode";
import AppImage from "../common/AppImage";
import {
  getAwardDescription,
  getAwardName,
  getNomineeExtraImage,
  getNomineeImage,
  getNomineeName,
  getNomineeVideo,
  getVotingAwardIds,
} from "../../utils/awards";
import { getPointsForPosition, getRequiredPositions, validateRankingSelection } from "../../utils/voting";

const Voting = ({ premioIds }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const accessMode = location.state?.accessMode || getStoredAccessMode();
  const isPremium = isPremiumAccess(accessMode) || Boolean(location.state?.isPremium);
  const [resolvedPremioIds, setResolvedPremioIds] = useState(Array.isArray(premioIds) ? premioIds : []);
  const [premio, setPremio] = useState(null);
  const [nominados, setNominados] = useState([]);
  const [selectedVotes, setSelectedVotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [premiosData, setPremiosData] = useState({});
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMediaURL, setSelectedMediaURL] = useState("");
  const [isMediaVideo, setIsMediaVideo] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const user = auth.currentUser;

  const requiredPositions = useMemo(() => getRequiredPositions(nominados.length), [nominados.length]);
  const currentAwardName = getAwardName(premio);

  useEffect(() => {
    let cancelled = false;

    const loadAwardIds = async () => {
      if (Array.isArray(premioIds) && premioIds.length > 0) {
        setResolvedPremioIds(premioIds);
        return;
      }

      try {
        const premiosSnapshot = await getDocs(collection(db, "premios"));
        const awards = premiosSnapshot.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }));

        if (!cancelled) {
          setResolvedPremioIds(getVotingAwardIds(awards));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error al cargar la lista de premios:", error);
          setLoadError("No hemos podido cargar las categorias disponibles.");
          setLoading(false);
        }
      }
    };

    loadAwardIds();

    return () => {
      cancelled = true;
    };
  }, [premioIds]);

  useEffect(() => {
    let cancelled = false;

    const fetchPremioAndNominados = async () => {
      if (!resolvedPremioIds.length) {
        setLoading(false);
        return;
      }

      const premioId = resolvedPremioIds[currentIndex];

      if (!premioId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");
      setFeedback({ type: "", text: "" });
      setSelectedVotes({});
      setHasUnsavedChanges(false);

      if (premiosData[premioId]) {
        if (!cancelled) {
          setPremio(premiosData[premioId].premio);
          setNominados(premiosData[premioId].nominados);
          setLoading(false);
        }
        return;
      }

      try {
        const premioRef = doc(db, "premios", premioId);
        const premioDoc = await getDoc(premioRef);

        if (!premioDoc.exists()) {
          throw new Error(`El premio ${premioId} no existe.`);
        }

        const premioData = premioDoc.data();
        const nominadosSnapshot = await getDocs(collection(premioRef, "nominados"));
        const nominadosList = nominadosSnapshot.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }));

        if (!cancelled) {
          setPremiosData((previousData) => ({
            ...previousData,
            [premioId]: { premio: premioData, nominados: nominadosList },
          }));
          setPremio(premioData);
          setNominados(nominadosList);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error al cargar el premio:", error);
          setLoadError("No hemos podido cargar esta categoria. Intentalo de nuevo.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPremioAndNominados();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, premiosData, resolvedPremioIds]);

  const handleSelectVote = (nominadoId, vote) => {
    setSelectedVotes((previousVotes) => ({
      ...previousVotes,
      [nominadoId]: vote,
    }));
    setHasUnsavedChanges(true);
    setFeedback((currentFeedback) =>
      currentFeedback.type === "success" ? { type: "", text: "" } : currentFeedback
    );
  };

  const handleShowMedia = (mediaURL, mediaIsVideo) => {
    setSelectedMediaURL(mediaURL);
    setIsMediaVideo(mediaIsVideo);
    setShowMediaModal(true);
  };

  const handleCloseMedia = () => {
    setShowMediaModal(false);
    setSelectedMediaURL("");
    setIsMediaVideo(false);
  };

  useEffect(() => {
    if (!showMediaModal) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleCloseMedia();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [showMediaModal]);

  const handleSubmitVotes = async () => {
    if (!user) {
      setFeedback({
        type: "error",
        text: "Tu sesion ha expirado. Vuelve a iniciar sesion.",
      });
      navigate("/login", { replace: true });
      return;
    }

    if (!validateRankingSelection(selectedVotes, nominados.length)) {
      setFeedback({
        type: "error",
        text: `Completa los puestos ${requiredPositions.join(", ")} antes de guardar esta votacion.`,
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: "", text: "" });

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userAwardVotes = {};

      for (const [nominadoId, votePosition] of Object.entries(selectedVotes)) {
        const points = getPointsForPosition(votePosition, nominados.length, isPremium);
        userAwardVotes[nominadoId] = points;

        const nominadoRef = doc(
          db,
          "premios",
          resolvedPremioIds[currentIndex],
          "nominados",
          nominadoId
        );
        const nominadoDoc = await getDoc(nominadoRef);
        const nominadoData = nominadoDoc.data() || {};
        const updatedVotedUsers = {
          ...(nominadoData.votedUsers || {}),
          [user.uid]: {
            email: user.email || "",
            timestamp: new Date().toISOString(),
            vote: points,
          },
        };
        const totalPoints = Object.values(updatedVotedUsers).reduce(
          (sum, userVote) => sum + (userVote.vote || 0),
          0
        );

        await updateDoc(nominadoRef, {
          votedUsers: updatedVotedUsers,
          votes: totalPoints,
        });
      }

      await setDoc(
        userDocRef,
        {
          [currentAwardName]: userAwardVotes,
        },
        { merge: true }
      );

      setFeedback({
        type: "success",
        text: `Tus votos de ${currentAwardName} se han guardado correctamente.`,
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error al registrar los votos:", error);
      setFeedback({
        type: "error",
        text: `No hemos podido guardar tus votos de ${currentAwardName}. Intentalo otra vez.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoHome = () => {
    if (isSubmitting) {
      return;
    }

    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        "Tienes cambios sin guardar en esta categoria. Si vuelves ahora al home, perderas esta seleccion no guardada. Quieres salir igualmente?"
      );

      if (!shouldLeave) {
        return;
      }
    }

    navigate("/home", { state: { accessMode, isPremium } });
  };

  if (loading) {
    return (
      <div className="voting-shell">
        <div className="voting-loader-card">
          <p className="section-kicker">Tofu Awards II</p>
          <h1>Cargando categoria</h1>
          <p>Estamos preparando los nominados y la siguiente votacion.</p>
        </div>
      </div>
    );
  }

  if (loadError || !premio || !resolvedPremioIds.length) {
    return (
      <div className="voting-shell">
        <div className="voting-loader-card error">
          <p className="section-kicker">Error de carga</p>
          <h1>No se ha podido abrir esta votacion</h1>
          <p>{loadError || "Aun no hay categorias publicas listas para votar."}</p>
          <button className="button-next-back" onClick={() => navigate("/home")}>
            Volver a la home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="voting-shell">
      <section className="voting-header-card">
        <div>
          <p className="section-kicker">
            Categoria {currentIndex + 1} de {resolvedPremioIds.length}
          </p>
          <h1 className="VotingTitle">{currentAwardName}</h1>
          <p className="subtitle">{getAwardDescription(premio)}</p>
        </div>

        <div className="score-card compact">
          <p className="score-card-label">Estado de tu voto</p>
          <strong>{isPremium ? "Voto premium activo" : "Voto estandar"}</strong>
          <span>Ordena a los nominados y guarda la votacion de esta categoria cuando termines.</span>
        </div>
      </section>

      <div className="progress-bar" aria-label="Navegacion entre categorias">
        {resolvedPremioIds.map((premioId, index) => (
          <button
            key={premioId}
            type="button"
            className={`progress-dot ${currentIndex === index ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Ir a la categoria ${index + 1}`}
          >
            {String(index + 1).padStart(2, "0")}
          </button>
        ))}
      </div>

      {feedback.text ? (
        <p className={`feedback-message ${feedback.type}`} aria-live="polite">
          {feedback.text}
        </p>
      ) : null}

      <div className="nominados-container">
        {nominados.map((nominado) => {
          const nomineeName = getNomineeName(nominado);
          const nomineeImage = getNomineeImage(nominado);
          const nomineeVideo = getNomineeVideo(nominado);
          const nomineeExtraImage = getNomineeExtraImage(nominado);

          return (
            <article key={nominado.id} className="nominado-card">
              <div className="nominado-image-wrapper">
                {nomineeImage ? (
                  <AppImage
                    src={nomineeImage}
                    alt={nomineeName}
                    className="nominado-card-image"
                    fallbackClassName="nominado-placeholder"
                  />
                ) : (
                  <div className="nominado-placeholder">Sin imagen</div>
                )}
              </div>

              <div className="nominado-copy">
                <h2>{nomineeName}</h2>
                <div className="nominado-options">
                  {nomineeVideo ? (
                    <button
                      type="button"
                      className="videoButton"
                      onClick={() => handleShowMedia(nomineeVideo, true)}
                    >
                      Ver video
                    </button>
                  ) : null}
                  {nomineeExtraImage ? (
                    <button
                      type="button"
                      className="videoButton"
                      onClick={() => handleShowMedia(nomineeExtraImage, false)}
                    >
                      Ver imagen
                    </button>
                  ) : null}
                </div>
              </div>

              <label className="vote-field">
                <span>Posicion</span>
                <select
                  className={`vote-select ${selectedVotes[nominado.id] ? "" : "is-placeholder"}`.trim()}
                  onChange={(event) => handleSelectVote(nominado.id, event.target.value)}
                  value={selectedVotes[nominado.id] || ""}
                >
                  <option value="">Selecciona</option>
                  {requiredPositions.map((position) => (
                    <option key={`${nominado.id}-${position}`} value={position}>
                      {position}o puesto
                    </option>
                  ))}
                </select>
              </label>
            </article>
          );
        })}
      </div>

      <div className="voting-footer">
        <div className="navigation-buttons">
          <button className="button-next-back" type="button" onClick={handleGoHome} disabled={isSubmitting}>
            Volver al home
          </button>
          <button
            className="button-next-back"
            type="button"
            onClick={() => setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0))}
            disabled={currentIndex === 0}
          >
            Categoria anterior
          </button>
          <button
            className="button-next-back"
            type="button"
            onClick={() =>
              setCurrentIndex((previousIndex) => Math.min(previousIndex + 1, resolvedPremioIds.length - 1))
            }
            disabled={currentIndex === resolvedPremioIds.length - 1}
          >
            Siguiente categoria
          </button>
        </div>

        <div className="voting-actions">
          <button className="button-voting" type="button" onClick={handleSubmitVotes} disabled={isSubmitting}>
            {isSubmitting ? "Guardando tus votos..." : "Guardar votacion de esta categoria"}
          </button>
          <button
            className="button-final-vote"
            type="button"
            onClick={() => navigate("/special-awards/intro", { state: { accessMode, isPremium } })}
          >
            Acceder a los honores especiales
          </button>
        </div>
      </div>

      {showMediaModal ? (
        <div className="modal-overlay" onClick={handleCloseMedia}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="modal-close-button" onClick={handleCloseMedia}>
              Cerrar
            </button>
            <div className="modal-media-container">
              {isMediaVideo ? (
                <video id="modal-video" controls autoPlay preload="auto">
                  <source src={selectedMediaURL} type="video/mp4" />
                  Tu navegador no soporta el video.
                </video>
              ) : (
                <AppImage
                  src={selectedMediaURL}
                  alt="Contenido del nominado"
                  className="modal-media-image"
                  fallbackClassName="modal-media-fallback"
                  fallbackText="No se ha podido cargar esta imagen"
                  loading="eager"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Voting;
